'use client';
import IconArrowLeft from '@/assets/icons/icon-carousel-next.svg';
import { TopProductCategoriesQuery } from '@/graphql';
import { mapEdgesToItems } from '@/helpers/maps';
import { useTranslatedData } from '@/hooks/useTranslatedData';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import styles from './best-carousel.module.scss';

type TabProps = {
  categoryData: TopProductCategoriesQuery | undefined;
  categoryLoading: Boolean;
  activeCategoryId: string | null;
  setActiveCategoryId: (categoryId: string | null) => void;
  brandPage?: boolean;
};

const BestCategoriesCarousel = ({
  categoryData,
  categoryLoading,
  activeCategoryId,
  setActiveCategoryId,
  brandPage,
}: TabProps) => {
  const splideRef = useRef<any>(null);
  const mappedData = mapEdgesToItems(categoryData?.topProductCategories);
  const translatedData = useTranslatedData(mappedData, ['name']);
  const t = useTranslations('General');
  const goToNextSlide = () => {
    const splide = splideRef.current?.splide;
    if (splide) {
      splide.go('>');
    }
  };
  return (
    <div className={styles.carousel}>
      {categoryLoading ? (
        <div className={styles.skeletonContainer}>
          {' '}
          {Array.from({ length: 13 }).map((_, index) => (
            <div key={index} className={`${styles.skeleton}`}>
              <Skeleton />
            </div>
          ))}
        </div>
      ) : (
        <Splide
          ref={splideRef}
          options={{
            rewind: true,
            updateOnMove: true,
            fixedWidth: 'auto',
            perPage: 3,
            perMove: 1,
            arrows: false,
            pagination: false,
            gap: '8px',
            padding: { right: '40px' },
            breakpoints: {
              1024: {
                perPage: 3,
                gap: '8px',
              },
              768: {
                perPage: 3,
                gap: '8px',
              },
              480: {
                perPage: 1,
                gap: '8px',
              },
            },
          }}
          hasTrack={false}
          style={{ padding: '0' }}
        >
          <SplideTrack>
            <SplideSlide key="all-products">
              <button
                type="button"
                onClick={() => setActiveCategoryId(null)}
                className={`${styles.slide} ${activeCategoryId === null && styles.slideActive}`}
              >
                <div className={styles.texts}>
                  <h3>{t('all-products')}</h3>
                </div>
              </button>
            </SplideSlide>
            {categoryData &&
              (() => {
                const getDisplayCategory = (item: any) => {
                  return item.level === 2 ? item : item.parent;
                };

                const getTotalCount = (item: any) => {
                  const category = getDisplayCategory(item);
                  return category?.products?.totalCount || 0;
                };

                return brandPage
                  ? translatedData
                      .filter(item => {
                        const category = getDisplayCategory(item);
                        return (
                          category?.level === 2 &&
                          category.products?.totalCount > 0
                        );
                      })
                      .sort((a, b) => getTotalCount(b) - getTotalCount(a))
                      .map(item => {
                        const displayCategory = getDisplayCategory(item);
                        const displayName = displayCategory?.name;

                        return (
                          <SplideSlide key={item.id}>
                            <button
                              type="button"
                              onClick={() => setActiveCategoryId(item.id)}
                              className={`${styles.slide} ${
                                activeCategoryId === item.id &&
                                styles.slideActive
                              }`}
                            >
                              <div className={styles.texts}>
                                <h3>{displayName}</h3>
                              </div>
                            </button>
                          </SplideSlide>
                        );
                      })
                  : (() => {
                      const renderedNames = new Set<string>();

                      return translatedData
                        .filter(item => {
                          if (item?.level !== 1) return false;

                          const nameToCheck = item.name;
                          if (renderedNames.has(nameToCheck)) return false;

                          renderedNames.add(nameToCheck);
                          return true;
                        })
                        .map(item => (
                          <SplideSlide key={item.id}>
                            <button
                              type="button"
                              onClick={() => setActiveCategoryId(item.id)}
                              className={`${styles.slide} ${
                                activeCategoryId === item.id &&
                                styles.slideActive
                              }`}
                            >
                              <div className={styles.texts}>
                                <h3>{item.name}</h3>
                              </div>
                            </button>
                          </SplideSlide>
                        ));
                    })();
              })()}
          </SplideTrack>
          <button
            type="button"
            aria-label="Next slide"
            className={styles.carouselNextButton}
            onClick={goToNextSlide}
          >
            <IconArrowLeft />
          </button>
        </Splide>
      )}
    </div>
  );
};

export default BestCategoriesCarousel;
